import { get, isPlainObject, startCase } from 'lodash'
import { parseJsonString, readAnalysisResults, readCallOverview } from 'app/components/ai-agent-voice-ops/callDataDebug'
import { noBookingReasonLabel } from 'app/components/ai-agent-voice-ops/voiceAgentOpsMetrics'
import type { BadgeColor } from 'app/components/ui/badge'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { CallAi, CallPerson } from 'app/generated/graphql'

/**
 * Reshapes everything AI extracted from a call into labelled groups for the
 * Data Collection tab: ElevenLabs' post-call analysis (`CallData.data`) and
 * Trackable's call processing (`Call.ai`). Missing values stay in the groups
 * with no value, so a reviewer can see what the model did not capture.
 */

export type DataBadge = { label: string; color: BadgeColor }

export type DataPoint = {
  id: string
  label: string
  value?: string
  badges?: DataBadge[]
  /** Sub-fields of a structured value, such as the insurance JSON. */
  fields?: DataPoint[]
  rationale?: string
}

export type DataGroup = { title: string; points: DataPoint[] }

const OTHER_GROUP = `Other`

type KnownKey = { group: string; label: string; trueColor?: BadgeColor }

/**
 * The data-collection items configured on the ElevenLabs agents, in display
 * order. Items not listed here still render, under Other, so a new item added
 * in ElevenLabs shows up without a code change.
 */
const ELEVENLABS_KEYS = new Map<string, KnownKey>([
  [`appointment_requested`, { group: `Outcome`, label: `Appointment requested` }],
  [`appointment_booked`, { group: `Outcome`, label: `Appointment booked`, trueColor: `green` }],
  [`no_booking_reason`, { group: `Outcome`, label: `Why not booked` }],
  [`emergency`, { group: `Outcome`, label: `Emergency`, trueColor: `amber` }],
  [`transferred`, { group: `Outcome`, label: `Transferred to office` }],
  [`insurance`, { group: `Insurance`, label: `Insurance details` }],
  [`benefits_check`, { group: `Insurance`, label: `Caller has insurance` }],
  [`insurance_discussed`, { group: `Insurance`, label: `Insurance discussed` }],
  [`availability_queried`, { group: `Conversation`, label: `Checked availability` }],
  [`pricing_discussed`, { group: `Conversation`, label: `Pricing discussed` }],
  [`mentioned_offer`, { group: `Conversation`, label: `New-patient offer mentioned` }],
  [`mentioned_consultation`, { group: `Conversation`, label: `Consultation mentioned` }],
  [`apologized`, { group: `Conversation`, label: `Agent apologized` }],
  [`error`, { group: `Agent Quality`, label: `Error`, trueColor: `red` }],
  [`spoke_json`, { group: `Agent Quality`, label: `Spoke raw data aloud`, trueColor: `red` }],
  [`tool_error`, { group: `Agent Quality`, label: `Tool error`, trueColor: `red` }],
  [`tool_timeout`, { group: `Agent Quality`, label: `Tool timeout`, trueColor: `red` }],
  [`tool_duplicate`, { group: `Agent Quality`, label: `Duplicate booking call`, trueColor: `red` }],
])

const ELEVENLABS_GROUPS = [`Outcome`, `Insurance`, `Conversation`, `Agent Quality`, OTHER_GROUP]
const KEY_ORDER = [...ELEVENLABS_KEYS.keys()]
const EVALUATION_COLOR: Record<string, BadgeColor> = { success: `green`, failure: `red` }

/** ElevenLabs writes "null" for an item it could not fill, so treat that as blank too. */
function isBlank(value: unknown): boolean {
  return value == null || value === `` || value === `null`
}

/** A Yes/No badge, or none when the value was never extracted. */
function flag(value: boolean | null | undefined, trueColor: BadgeColor = `blue`): DataBadge[] | undefined {
  if (value == null) return undefined
  return [value ? { label: `Yes`, color: trueColor } : { label: `No`, color: `gray` }]
}

function toDataPoint(id: string, label: string, raw: unknown, trueColor?: BadgeColor): DataPoint {
  // ElevenLabs returns structured string items as `json{...}`; drop the marker so they parse.
  const value = parseJsonString(typeof raw === `string` ? raw.replace(/^\s*json(?=\s*[{[])/, ``) : raw)
  if (typeof value === `boolean`) return { id, label, badges: flag(value, trueColor) }
  if (isBlank(value)) return { id, label }
  if (isPlainObject(value)) {
    return {
      id,
      label,
      fields: Object.entries(value as Record<string, unknown>).map(([key, fieldValue]) =>
        toDataPoint(key, startCase(key), fieldValue),
      ),
    }
  }
  if (id === `no_booking_reason`) return { id, label, value: noBookingReasonLabel(String(value)) }
  return { id, label, value: typeof value === `object` ? JSON.stringify(value) : String(value) }
}

function readDataCollection(
  extractedData: Record<string, unknown> | undefined,
  payload: unknown,
): { id: string; value: unknown; rationale?: string }[] {
  // extractedData is the editable source of truth (id -> stringified value); a
  // super-user edit lands here, so prefer it. Fall back to the raw payload's
  // data_collection_results (which also carries rationale) for records not yet
  // backfilled.
  if (extractedData && Object.keys(extractedData).length) {
    return Object.entries(extractedData).map(([id, value]) => ({ id, value }))
  }
  // `get` on an `unknown` object types its result as undefined; `object` selects the general overload.
  const results: unknown = get(payload as object, `transcriptWebhook.analysis.data_collection_results`)
  return Object.entries(isPlainObject(results) ? (results as Record<string, unknown>) : {}).map(([id, raw]) => {
    // Each item is `{ value, rationale }`; a bare value is kept as-is.
    const rationale: unknown = get(raw as object, `rationale`)
    return {
      id,
      value: isPlainObject(raw) ? get(raw as object, `value`) : raw,
      rationale: typeof rationale === `string` ? rationale : undefined,
    }
  })
}

export type ElevenLabsCollection = {
  title?: string
  summary?: string
  callSuccessful?: DataBadge
  groups: DataGroup[]
}

/**
 * ElevenLabs' post-call analysis, or undefined when the payload has none. Accepts
 * the ElevenLabs CallData record: `extractedData` (the editable source of truth
 * for the data-collection values) plus the raw `data` payload the overview and
 * evaluation readers still need.
 */
export function elevenLabsCollection(callData: unknown): ElevenLabsCollection | undefined {
  const record = isPlainObject(callData) ? (callData as Record<string, unknown>) : undefined
  const extractedData = isPlainObject(record?.extractedData)
    ? (record!.extractedData as Record<string, unknown>)
    : undefined
  // The raw payload lives under `.data`; tolerate being handed the payload directly.
  const payload = record && `data` in record ? record.data : callData

  const overview = readCallOverview(payload)
  const items = readDataCollection(extractedData, payload)
  const evaluations = readAnalysisResults(payload).filter((result) => result.kind === `Evaluation`)
  if (!items.length && !evaluations.length && !overview.summary) return undefined

  const rank = new Map(KEY_ORDER.map((id, index) => [id, index]))
  const sorted = [...items].sort((a, b) => (rank.get(a.id) ?? KEY_ORDER.length) - (rank.get(b.id) ?? KEY_ORDER.length))
  const byGroup = new Map<string, DataPoint[]>()
  for (const item of sorted) {
    const known = ELEVENLABS_KEYS.get(item.id)
    const group = known?.group ?? OTHER_GROUP
    const point = toDataPoint(item.id, known?.label ?? startCase(item.id), item.value, known?.trueColor)
    byGroup.set(group, [...(byGroup.get(group) ?? []), { ...point, rationale: item.rationale }])
  }

  const groups: DataGroup[] = ELEVENLABS_GROUPS.flatMap((title) => {
    const points = byGroup.get(title)
    return points ? [{ title, points }] : []
  })
  if (evaluations.length) {
    groups.push({
      title: `Evaluation Criteria`,
      points: evaluations.map((evaluation) => {
        const result = isBlank(evaluation.value) ? `unknown` : String(evaluation.value)
        return {
          id: evaluation.id,
          label: startCase(evaluation.id),
          badges: [{ label: startCase(result), color: EVALUATION_COLOR[result] ?? `gray` }],
          rationale: evaluation.rationale,
        }
      }),
    })
  }

  return {
    title: overview.summaryTitle,
    summary: overview.summary,
    callSuccessful: overview.callSuccessful
      ? { label: startCase(overview.callSuccessful), color: EVALUATION_COLOR[overview.callSuccessful] ?? `gray` }
      : undefined,
    groups,
  }
}

export type TrackablePerson = { key: string; name: string; isCaller: boolean; groups: DataGroup[] }

export type TrackableCollection = {
  overview: DataGroup
  people: TrackablePerson[]
  extraction: DataGroup
}

function trackablePerson(p: CallPerson, index: number): TrackablePerson {
  return {
    key: `${p.index ?? index}-${p.name ?? ``}`,
    name: startCase(p.name || `Unknown`),
    isCaller: !!p.isCaller,
    groups: [
      {
        title: `Identity`,
        points: [
          { id: `name`, label: `Name`, value: p.name || undefined },
          { id: `phone`, label: `Phone`, value: formatPhoneNumber(p.phone) || undefined },
          { id: `email`, label: `Email`, value: p.email || undefined },
          {
            id: `birthDate`,
            label: `Birth date`,
            value: formatDate({ value: p.birthDate, dateOnly: true }) || undefined,
          },
          { id: `relationship`, label: `Relationship`, value: p.relationship || undefined },
          { id: `isCaller`, label: `Caller`, badges: flag(p.isCaller) },
        ],
      },
      {
        title: `Visit`,
        points: [
          { id: `patientType`, label: `Patient type`, value: p.patientType || undefined },
          { id: `treatment`, label: `Treatment`, value: p.treatment || undefined },
          { id: `serviceRequested`, label: `Service requested`, value: p.serviceRequested || undefined },
          { id: `referral`, label: `Referral`, value: p.referral || undefined },
          { id: `insuranceProvider`, label: `Insurance provider`, value: p.insuranceProvider || undefined },
        ],
      },
      {
        title: `Appointment`,
        points: [
          { id: `appointmentRequested`, label: `Requested`, badges: flag(p.appointmentRequested) },
          { id: `appointmentScheduled`, label: `Scheduled`, badges: flag(p.appointmentScheduled, `green`) },
          {
            id: `appointmentDate`,
            label: `Date`,
            value: formatDate({ value: p.appointmentDate, dateOnly: true }) || undefined,
          },
          { id: `appointmentTime`, label: `Time`, value: p.appointmentTime || undefined },
          { id: `reasonForNotScheduling`, label: `Reason not scheduled`, value: p.reasonForNotScheduling || undefined },
        ],
      },
      {
        title: `Outcomes`,
        points: [
          {
            id: `outcomes`,
            label: `Outcomes`,
            badges: p.outcomes?.length ? p.outcomes.map((outcome) => ({ label: outcome, color: `gray` })) : undefined,
          },
        ],
      },
    ],
  }
}

/** Trackable's call processing extraction, or undefined when the call was not processed. */
export function trackableCollection(ai?: CallAi | null): TrackableCollection | undefined {
  if (!ai) return undefined
  const tokens = ai.tokenUsage
  return {
    overview: {
      title: `Call`,
      points: [
        { id: `summary`, label: `Summary`, value: ai.summary || undefined },
        { id: `actionItems`, label: `Action items`, value: ai.actionItems || undefined },
        { id: `answerer`, label: `Answered by`, value: ai.answerer || undefined },
      ],
    },
    // The caller first, as the Summary tab lists them.
    people: (ai.people ?? []).map(trackablePerson).sort((a, b) => Number(b.isCaller) - Number(a.isCaller)),
    extraction: {
      title: `Extraction`,
      points: [
        { id: `model`, label: `Model`, value: ai.model || undefined },
        { id: `channel`, label: `Channel`, value: ai.channel || undefined },
        {
          id: `extractionDate`,
          label: `Extracted`,
          value: formatDate({ value: ai.extractionDate, useLocalUtc: true }) || undefined,
        },
        {
          id: `tokens`,
          label: `Tokens (in / out / total)`,
          value: tokens ? [tokens.inputTokens, tokens.outputTokens, tokens.totalTokens].join(` / `) : undefined,
        },
      ],
    },
  }
}
